import React from "react";
import { FlaskConical, Check, Clock, ArrowRight } from "lucide-react";
import { clinicInfo } from "../mock";

const POINTS = [
  "Routine blood tests, screening panels & INR monitoring",
  "All referrals accepted — including from other practices",
  "Samples collected immediately after your consultation",
  "Bulk billing available for eligible patients"
];

export default function Pathology() {
  return (
    <section id="pathology" className="relative px-4 md:px-8 pb-4">
      <div className="relative mx-auto max-w-[1400px] rounded-[36px] bg-gradient-to-br from-[#1a2464] to-[#242f7e] text-white overflow-hidden">
        <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#3dc9b3]/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#3dc9b3]/10 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 p-8 md:p-14 lg:p-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white font-semibold text-xs px-4 py-1.5 rounded-full mb-5">
              <FlaskConical className="w-3.5 h-3.5 text-[#3dc9b3]" />
              On-site partnership
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight">
              4Cyte Pathology <br />
              <span className="text-[#3dc9b3]">right here on-site</span>
            </h2>
            <p className="text-white/80 leading-relaxed mt-6 max-w-xl">
              Our patients enjoy the added convenience of a fully-serviced 4Cyte Pathology collection room operating directly inside our clinic, Monday to Friday. Whether you need a routine blood test, a screening panel or specialised investigations, samples can be taken immediately after your consultation — no extra trip, no separate booking.
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {POINTS.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#3dc9b3] grid place-items-center">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-white/90 text-[15px]">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white text-sm px-5 py-2.5 rounded-full">
                <Clock className="w-4 h-4 text-[#3dc9b3]" />
                Open Monday to Friday
              </div>
              <a
                href={`tel:${clinicInfo.phoneRaw}`}
                className="group inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold pl-5 pr-2 py-2 rounded-full hover:bg-[#34b39f]"
              >
                Call {clinicInfo.phone}
                <span className="w-8 h-8 rounded-full bg-white/20 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight className="w-4 h-4 text-white" />
                </span>
              </a>
            </div>
          </div>

          {/* 4Cyte badge card */}
          <div className="relative">
            <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-[#3dc9b3]/25 to-transparent blur-2xl" />
            <div className="relative bg-white rounded-[28px] p-10 md:p-12 text-center shadow-2xl">
              <div className="text-[#1a2464] text-xs font-bold tracking-[0.25em] uppercase mb-3">
                Trusted partner
              </div>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-[#e11d48]" />
                <span className="w-6 h-6 rounded-full bg-[#e11d48]/70" />
                <span className="w-6 h-6 rounded-full bg-[#e11d48]/40" />
                <span className="w-6 h-6 rounded-full bg-[#e11d48]/20" />
              </div>
              <div className="text-[#e11d48] text-4xl md:text-5xl font-extrabold tracking-tight">
                4Cyte
              </div>
              <div className="text-[#1a2464] text-lg md:text-xl font-semibold tracking-tight mt-1">
                Pathology
              </div>
              <div className="h-px bg-[#eee6f7] my-6" />
              <div className="text-[#4a4a63] text-sm leading-relaxed">
                A national pathology provider trusted by GPs and specialists across Australia — now collecting directly at Beaudesert General Practice.
              </div>
              <div className="mt-6 inline-flex items-center gap-2 bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-xs px-4 py-1.5 rounded-full">
                All referrals accepted
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
